"use client";

import React from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Toaster } from "sonner";
import useToast from "@/hooks/useToast";
import { useLanguageStore } from "@/providers/useLanguageStore";

interface FormData {
  nombre: string;
  apellido: string;
  email: string;
  mensaje: string;
}

const ContactForm: React.FC = () => {
  const { translations } = useLanguageStore();
  const { showSuccess, showError } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      await axios.post("/api/send-email", data);
      showSuccess(
        translations.successMessage || "¡El mensaje se ha enviado con éxito!"
      );
      reset();
    } catch (error) {
      console.error(error);
      showError(
        translations.errorMessage || "Hubo un problema al enviar el mensaje."
      );
    }
  };

  return (
    <div className="bg-white px-8 pt-2 md:p-8 mx-4 md:mx-4 lg:mx-16">
      <h3 className="text-3xl font-bold text-red-600 mb-6">
        {translations.contactTitle || "Charlemos"}
      </h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              {...register("nombre", {
                required:
                  translations.requiredName || "El nombre es obligatorio",
                maxLength: {
                  value: 10,
                  message: translations.nameMaxLength || "Máximo 10 caracteres",
                },
              })}
              maxLength={11}
              placeholder={translations.namePlaceholder || "Nombre"}
              className="w-full text-black border-b-2 border-red-600 focus:outline-none focus:border-red-700"
            />
            {errors.nombre && (
              <span className="text-red-600 text-sm">
                {errors.nombre.message}
              </span>
            )}
          </div>

          <div>
            <input
              type="text"
              {...register("apellido", {
                required:
                  translations.requiredSurname || "El apellido es obligatorio",
                maxLength: {
                  value: 10,
                  message:
                    translations.surnameMaxLength || "Máximo 10 caracteres",
                },
              })}
              maxLength={11}
              placeholder={translations.surnamePlaceholder || "Apellido"}
              className="w-full text-black border-b-2 border-red-600 focus:outline-none focus:border-red-700"
            />
            {errors.apellido && (
              <span className="text-red-600 text-sm">
                {errors.apellido.message}
              </span>
            )}
          </div>
        </div>

        <div>
          <input
            type="email"
            {...register("email", {
              required: translations.requiredEmail || "El email es obligatorio",
              maxLength: {
                value: 60,
                message: translations.emailMaxLength || "Máximo 60 caracteres",
              },
              pattern: {
                value: /^[^@]+@[^@]+\.[^@]+$/,
                message: translations.invalidEmail || "Email no válido",
              },
            })}
            maxLength={60}
            placeholder={translations.emailPlaceholder || "Email"}
            className="w-full text-black border-b-2 border-red-600 focus:outline-none focus:border-red-700"
          />
          {errors.email && (
            <span className="text-red-600 text-sm">{errors.email.message}</span>
          )}
        </div>

        <div>
          <textarea
            {...register("mensaje", {
              required:
                translations.requiredMessage || "El mensaje es obligatorio",
            })}
            rows={4}
            placeholder={translations.messagePlaceholder || "Mensaje"}
            className="w-full text-black border-b-2 border-red-600 focus:outline-none focus:border-red-700 resize-none"
          ></textarea>
          {errors.mensaje && (
            <span className="text-red-600 text-sm">
              {errors.mensaje.message}
            </span>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-red-600 text-white font-bold py-2 px-6 hover:bg-red-700 transition-all w-full md:w-auto disabled:bg-gray-400"
        >
          {isSubmitting
            ? translations.sending || "Enviando..."
            : translations.send || "Enviar"}
        </button>
      </form>
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default ContactForm;
